import { element } from '../../global/index.js';
import { Labels } from '../../global/labels.js';
import styles from './ui-slides-pagination.css';
import { keyCodes, TabIndex } from '../../global/keyboard.js';
import { debounce } from '../../global/helpers.js';
import { Component } from '../../decorators/component.decorator.js';
import { UIElement } from '../ui-element.js';
import { Attribute } from '../../decorators/attribute.decorator.js';
import { Children } from '../../decorators/child.decorator.js';
import { ToggleEventListenerAction } from './ui-slides.const.js';
import type { ThumbnailsPaginationData } from './ui-slides.type.js';
import type { TranslationLabels } from '../../types/labels.type.js';
import type { EventHandler } from '../../global/utils.type.js';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-slides-pagination
 * @augments {UIElement}
 * @alias UISlidesPagination
 * @element ui-slides-pagination
 * @classdesc Represents a class for <code>ui-slides-pagination</code> element.
 * Pagination element for carousel like element.
 * @property {number} [items=0] {@attr items} Number of elements
 * the carousel has.
 * @property {number} [activeIndex=0] {@attr mobile-index}
 * Index of the currently visible element.
 * @property {number} [containerId=''] {@attr container-id}
 * The id of the carousel container for accessibility.
 * @property {number} [thumbnails] {@attr thumbnails}
 * If the pagination should have thumbnails layout, only available for ui-offers as children.
 * @property {HTMLLIElement[]} paginationDotButtons - {@readonly} Shortcut to pagination dot buttons.
 * @property {HTMLButtonElement[]} thumbnailNavButtons - {@readonly} Shortcut to thumbnail left and right navigation buttons.
 * @property {HTMLLIElement[]} thumbnailButtons - {@readonly} Shortcut to pagination thumbnail buttons.
 * @slot
 * @example
 * <ui-slides-pagination
 *    items="3"
 *    container-id="i-am-id"
 * ></ui-slides-pagination>
 */
@Component({
    tag: 'ui-slides-pagination',
    styles,
})
export class UISlidesPagination extends UIElement {
    private readonly paginationDotButtonsClickHandlers: Array<() => void> = [];
    private readonly paginationDotButtonsKeyboardHandlers: Array<
        (event: KeyboardEvent) => void
    > = [];
    private readonly thumbnailNavButtonsClickHandlers: Array<() => void> = [];
    private readonly thumbnailNavButtonsKeyboardHandlers: Array<
        (event: KeyboardEvent) => void
    > = [];
    private readonly thumbnailButtonsClickHandlers: Array<() => void> = [];
    private readonly thumbnailButtonsKeyboardHandlers: Array<
        (event: KeyboardEvent) => void
    > = [];
    private readonly resizeHandler: () => void = this.onResize.bind(this);
    private readonly debouncedResizeHandler: () => void = debounce(
        this.resizeHandler,
        200
    ) as EventHandler;
    private thumbnailsButtonsElementsOffsetMax: number = 0;

    @Attribute() items = 0;
    @Attribute() activeIndex = 0;
    @Attribute(String) containerId!: string;
    @Attribute(Boolean) thumbnails!: boolean;

    @Children('.ui-slides-pagination__dot-button')
    paginationDotButtons!: NodeListOf<HTMLLIElement>;
    @Children('.ui-slides-pagination__thumbnail-nav-button')
    thumbnailNavButtons!: NodeListOf<HTMLButtonElement>;
    @Children('.ui-slides-pagination__thumbnail-button')
    thumbnailButtons!: NodeListOf<HTMLLIElement>;

    /**
     * @inheritDoc
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-slides', {
            titleNav: 'Slides navigation',
            prev: 'Previous',
            next: 'Next',
            slide: 'Slide',
        });
    }

    /**
     * Changes pagination buttons active state and dispatches pagination-change event.
     * @param {number} _newActiveIndex
     * @private
     */
    private onPaginationChange(_newActiveIndex: number): void {
        if (_newActiveIndex === this.activeIndex) {
            return;
        }

        const newActiveIndex: number =
            _newActiveIndex < 0
                ? _newActiveIndex + this.items
                : _newActiveIndex % this.items;

        this.dispatchCustomEvent('pagination-change', {
            activeIndex: newActiveIndex,
        });

        if (!this.thumbnails) {
            this.paginationDotButtons[this.activeIndex].classList.remove(
                '-active'
            );
            this.paginationDotButtons[newActiveIndex].classList.add('-active');
        } else {
            this.thumbnailButtons[this.activeIndex].classList.remove('-active');
            this.thumbnailButtons[newActiveIndex].classList.add('-active');
            this.style.setProperty(
                '--thumbnail-items-offset',
                `${Math.min(newActiveIndex, this.thumbnailsButtonsElementsOffsetMax)}`
            );
        }

        this.activeIndex = newActiveIndex;
    }

    /**
     * Keyboard event handler and changes the pagination to the index of the pagination button when
     * Enter or Space key is pressed while the pagination button element is focused.
     * @param {KeyboardEvent} event
     * @param {number} newActiveIndex
     * @private
     */
    private onKeyboard(event: KeyboardEvent, newActiveIndex: number): void {
        [keyCodes.ENTER, keyCodes.SPACE].includes(event.keyCode) &&
            this.onPaginationChange(newActiveIndex);
    }

    /**
     * Checks if the container is wider than the thumbnails buttons and depending
     * on that sets the thumbnail buttons offset.
     * @private
     */
    private onResize(): void {
        if (!this.thumbnails) {
            return;
        }

        this.classList.add('-static');

        const availableWidth: number = this.getBoundingClientRect().width - 40; // Horizontal padding 40px.
        // Thumbnail width 120px.
        if (availableWidth > this.items * 120) {
            this.thumbnailsButtonsElementsOffsetMax = 0;
            this.style.setProperty(
                '--thumbnail-items-offset',
                `${this.thumbnailsButtonsElementsOffsetMax}`
            );
            this.style.setProperty(
                '--visible-thumbnail-items',
                `${this.items}`
            );
            this.classList.add('hidden');
        } else {
            this.classList.remove('hidden');
            // Navigation arrow button width 40px, column gap 20px and thumbnail width 120px.
            const visibleThumbnailElements: number =
                Math.floor((availableWidth - (40 * 2 + 20 * 2)) / 120) || 1;
            this.thumbnailsButtonsElementsOffsetMax =
                this.items - visibleThumbnailElements;
            this.style.setProperty(
                '--thumbnail-items-offset',
                `${Math.min(this.activeIndex, this.thumbnailsButtonsElementsOffsetMax)}`
            );
            this.style.setProperty(
                '--visible-thumbnail-items',
                `${visibleThumbnailElements}`
            );
        }

        window.requestAnimationFrame(() => {
            this.classList.remove('-static');
        });
    }

    /**
     * Util for adding/removing event handlers from elements.
     * @param { NodeListOf<HTMLLIElement | HTMLButtonElement>} elements
     * @param {Function[]} clickHandlers
     * @param {Function[]} keyboardHandlers
     * @param {ToggleEventListenerAction | undefined} action
     * @private
     */
    private toggleEventListeners<
        T extends HTMLLIElement,
        U extends HTMLButtonElement,
    >(
        elements: NodeListOf<T | U>,
        clickHandlers: Array<() => void>,
        keyboardHandlers: Array<(event: KeyboardEvent) => void>,
        action: ToggleEventListenerAction = ToggleEventListenerAction.AddEventListener
    ): void {
        [].forEach.call(elements, (element: T | U, index: number): void => {
            element[action]('click', clickHandlers[index]);
            element[action](
                'keydown',
                keyboardHandlers[index] as EventListener
            );
        });
    }

    /**
     * @private
     */
    private addEventListeners(): void {
        this.toggleEventListeners(
            this.paginationDotButtons,
            this.paginationDotButtonsClickHandlers,
            this.paginationDotButtonsKeyboardHandlers
        );
        this.toggleEventListeners(
            this.thumbnailNavButtons,
            this.thumbnailNavButtonsClickHandlers,
            this.thumbnailNavButtonsKeyboardHandlers
        );
        this.toggleEventListeners(
            this.thumbnailButtons,
            this.thumbnailButtonsClickHandlers,
            this.thumbnailButtonsKeyboardHandlers
        );
        window.addEventListener('resize', this.debouncedResizeHandler);
    }

    /**
     * @private
     */
    private removeEventListeners(): void {
        this.toggleEventListeners(
            this.paginationDotButtons,
            this.paginationDotButtonsClickHandlers,
            this.paginationDotButtonsKeyboardHandlers,
            ToggleEventListenerAction.RemoveEventListener
        );
        this.toggleEventListeners(
            this.thumbnailNavButtons,
            this.thumbnailNavButtonsClickHandlers,
            this.thumbnailNavButtonsKeyboardHandlers,
            ToggleEventListenerAction.RemoveEventListener
        );
        this.toggleEventListeners(
            this.thumbnailButtons,
            this.thumbnailButtonsClickHandlers,
            this.thumbnailButtonsKeyboardHandlers,
            ToggleEventListenerAction.RemoveEventListener
        );
        window.removeEventListener('resize', this.debouncedResizeHandler);
    }

    /**
     * This sets the event handlers up once in the component lifetime and saves them
     * for reusing when reconnecting/disconnecting.
     * @param {number} elementsCount
     * @param {Function[]} clickHandlers
     * @param {Function[]} keyboardHandlers
     * @param {Function} clickHandlerGenerator
     * @param {Function} keyboardHandlerGenerator
     * @private
     */
    private setupEventHandlers(
        elementsCount: number,
        clickHandlers: Array<() => void>,
        keyboardHandlers: Array<(event: KeyboardEvent) => void>,
        clickHandlerGenerator: (index: number) => () => void,
        keyboardHandlerGenerator: (
            index: number
        ) => (event: KeyboardEvent) => void
    ): void {
        for (let index: number = 0; index < elementsCount; index++) {
            clickHandlers.push(clickHandlerGenerator(index));
            keyboardHandlers.push(keyboardHandlerGenerator(index));
        }
    }

    /**
     * Can be used to set the active slide index programmatically.
     * @param {number} newActiveIndex
     */
    setActiveIndex(newActiveIndex: number): void {
        this.onPaginationChange(newActiveIndex);
    }

    /**
     * For passing and setting thumbnail pagination data.
     * @param {ThumbnailsPaginationData} thumbnailsPaginationData
     */
    setThumbnailsPaginationData(
        thumbnailsPaginationData: ThumbnailsPaginationData
    ): void {
        [].forEach.call(
            this.thumbnailButtons,
            (thumbnailButtonElement: HTMLLIElement, index: number): void => {
                thumbnailButtonElement
                    .querySelector('img')!
                    .setAttribute(
                        'src',
                        thumbnailsPaginationData[index].thumbnailSrc
                    );
                thumbnailButtonElement.querySelector('h3')!.innerText =
                    thumbnailsPaginationData[index].thumbnailTitle;
            }
        );
    }

    /**
     * @private
     */
    private renderDotButtons(): void {
        const paginationDotButtons = Array.from({ length: this.items }).map(
            (_, index: number) =>
                element`
        <li
          class="ui-slides-pagination__dot-button${index === this.activeIndex ? ' -active' : ''}"
          role="button" tabindex="${TabIndex.Active}"
          aria-label="${UISlidesPagination.labels.slide} ${index + 1}"
          aria-controls="${this.containerId}"
        >
          <span class="ui-slides-pagination__dot"></span>
        </li>` as HTMLLIElement
        );

        this.append(element`<nav class="ui-slides-pagination__container">
      <ul class="ui-slides-pagination__dot-buttons">${paginationDotButtons}</ul>
    </nav>`);
    }

    /**
     * @private
     */
    private renderThumbnailButtons(): void {
        const thumbnailButtons = Array.from({ length: this.items }).map(
            (_, index: number) =>
                element`
        <li
          class="ui-slides-pagination__thumbnail-button${index === this.activeIndex ? ' -active' : ''}"
          role="button" tabindex="${TabIndex.Active}"
          aria-label="${UISlidesPagination.labels.slide} ${index + 1}"
          aria-controls="${this.containerId}"
        >
          <img src="" alt="" />
          <h3>${UISlidesPagination.labels.slide} ${index + 1}</h3>
        </li>` as HTMLLIElement
        );

        this.append(element`<nav class="ui-slides-pagination__container">
      <button
        class="ui-slides-pagination__thumbnail-nav-button"
        aria-controls="${this.containerId}"
        aria-label="${UISlidesPagination.labels.prev}"
      >
        <ui-icon class="-navicon" glyph="left" bgcolor="white"></ui-icon>
      </button>
      <div class="ui-slides-pagination__thumbnail-buttons-wrapper">
        <ul class="ui-slides-pagination__thumbnail-buttons">
          ${thumbnailButtons}
        </ul>
      </div>
      <button
        class="ui-slides-pagination__thumbnail-nav-button"
        aria-controls="${this.containerId}"
        aria-label="${UISlidesPagination.labels.next}"
      >
        <ui-icon class="-navicon" glyph="right" bgcolor="white"></ui-icon>
      </button>
    </nav>`);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (this.thumbnails) {
            this.renderThumbnailButtons();
            return;
        }

        this.renderDotButtons();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const paginationChangeHandler = this.onPaginationChange.bind(this);
        const keyboardHandler = this.onKeyboard.bind(this);

        this.setupEventHandlers(
            this.paginationDotButtons.length,
            this.paginationDotButtonsClickHandlers,
            this.paginationDotButtonsKeyboardHandlers,
            (index: number) => (): void => paginationChangeHandler(index),
            (index: number) =>
                (event: KeyboardEvent): void =>
                    keyboardHandler(event, index)
        );

        this.setupEventHandlers(
            this.thumbnailButtons.length,
            this.thumbnailButtonsClickHandlers,
            this.thumbnailButtonsKeyboardHandlers,
            (index: number) => (): void => paginationChangeHandler(index),
            (index: number) =>
                (event: KeyboardEvent): void =>
                    keyboardHandler(event, index)
        );

        this.setupEventHandlers(
            this.thumbnailNavButtons.length,
            this.thumbnailNavButtonsClickHandlers,
            this.thumbnailNavButtonsKeyboardHandlers,
            (index: number) => (): void => {
                const newActiveIndex: number =
                    this.activeIndex + (index === 0 ? -1 : 1);
                paginationChangeHandler(newActiveIndex);
            },
            (index: number) =>
                (event: KeyboardEvent): void => {
                    const newActiveIndex: number =
                        this.activeIndex + (index === 0 ? -1 : 1);
                    keyboardHandler(event, newActiveIndex);
                }
        );

        this.addEventListeners();
        this.resizeHandler();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.addEventListeners();
    }
}
